import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { useAdapter } from "@/store/site";
import { useNow } from "@/hooks/useNow";
import { Card } from "@/components/ui/Card";
import type { PricePoint } from "@/data/types";
import { formatHourMin, formatRelativeFuture } from "@/lib/format";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";

/**
 * Glanceable tariff strategy cue — answers "what's the BESS doing about
 * prices in the next few hours?" without making the operator read a chart.
 *
 *   - Now-window classification: cheap / mid / expensive (relative to the
 *     curve's quartiles).
 *   - Time to next price spike (above 75th pct) or trough (below 25th pct).
 *   - Suggested BESS action (charge / hold / discharge).
 */
export function TariffWindowBanner() {
  const adapter = useAdapter();
  const t = useT();
  const now = useNow(60_000);
  const [curve, setCurve] = useState<PricePoint[]>([]);
  useEffect(() => {
    let alive = true;
    const start = (() => { const d = new Date(); d.setMinutes(0, 0, 0); return d.getTime() - 60 * 60_000; })();
    adapter.prices(start, 36).then((c) => { if (alive) setCurve(c); });
    return () => { alive = false; };
  }, [adapter]);
  if (!curve.length) return null;

  const sorted = [...curve].map((c) => c.price).sort((a, b) => a - b);
  const q25 = sorted[Math.floor(sorted.length * 0.25)] ?? 0;
  const q75 = sorted[Math.floor(sorted.length * 0.75)] ?? 0;

  const currentIdx = curve.reduce((best, p, i) => Math.abs(p.ts - now) < Math.abs(curve[best].ts - now) ? i : best, 0);
  const current = curve[currentIdx];
  const nextSpike = curve.slice(currentIdx + 1).find((p) => p.price >= q75);
  const nextTrough = curve.slice(currentIdx + 1).find((p) => p.price <= q25);

  const window =
    current.price >= q75 ? "expensive" :
    current.price <= q25 ? "cheap" :
    "mid";

  const tone = window === "cheap" ? "ok" : window === "expensive" ? "warn" : "muted";
  const action =
    window === "cheap" ? { icon: ArrowDown, text: t("monitor.tariff.cheapAction"), tint: "text-price-cheap" } :
    window === "expensive" ? { icon: ArrowUp, text: t("monitor.tariff.peakAction"), tint: "text-price-dear" } :
    { icon: Minus, text: t("monitor.tariff.neutralAction"), tint: "text-text-muted" };
  const Icon = action.icon;

  return (
    <Card className={cn(
      "flex flex-wrap items-center gap-4 p-4",
      tone === "ok" && "border-price-cheap/30 bg-price-cheap/5",
      tone === "warn" && "border-price-dear/30 bg-price-dear/5",
    )}>
      <div className="flex items-center gap-3">
        <span className={cn("inline-grid h-10 w-10 place-items-center rounded-lg",
          tone === "ok" && "bg-price-cheap/15 text-price-cheap",
          tone === "warn" && "bg-price-dear/15 text-price-dear",
          tone === "muted" && "bg-surface-2 text-text-muted",
        )}>
          <Icon size={18} />
        </span>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-text-faint font-semibold">{t("monitor.tariff.now")}</div>
          <div className="text-2xl font-bold num">€{current.price.toFixed(0)}<span className="ml-1 text-xs text-text-muted">/MWh</span></div>
          {/* Action line drills into Revenue — the page that values what
              the suggested charge/discharge behaviour is worth (OVW-11). */}
          <Link
            to={carryTo("/revenue")}
            className={cn("block text-[11px] underline-offset-2 hover:underline", action.tint)}
          >
            {action.text}
          </Link>
        </div>
      </div>

      <div className="ml-auto flex flex-wrap gap-3 text-[11px] text-text-muted">
        <Pill label={t("monitor.tariff.q25")} value={`€${q25.toFixed(0)}`} />
        <Pill label={t("monitor.tariff.q75")} value={`€${q75.toFixed(0)}`} />
        {nextSpike && (
          <Pill label={t("monitor.tariff.nextPeak")} tone="warn"
            value={`${formatHourMin(nextSpike.ts)} (${formatRelativeFuture(nextSpike.ts - now)})`}
            sub={`€${nextSpike.price.toFixed(0)}/MWh`}
            to={carryTo("/market-price")} />
        )}
        {nextTrough && (
          <Pill label={t("monitor.tariff.nextTrough")} tone="ok"
            value={`${formatHourMin(nextTrough.ts)} (${formatRelativeFuture(nextTrough.ts - now)})`}
            sub={`€${nextTrough.price.toFixed(0)}/MWh`}
            to={carryTo("/market-price")} />
        )}
      </div>
    </Card>
  );
}

function Pill({ label, value, sub, tone = "muted", to }: { label: string; value: string; sub?: string; tone?: "ok" | "warn" | "muted"; to?: string }) {
  const cls = tone === "ok" ? "border-price-cheap/30 text-price-cheap" : tone === "warn" ? "border-price-dear/30 text-price-dear" : "border-edge text-text";
  const body = (
    <>
      <div className="text-[9px] uppercase tracking-wider text-text-faint">{label}</div>
      <div className="text-xs font-semibold num">{value}</div>
      {sub && <div className="text-[9px] text-text-muted num">{sub}</div>}
    </>
  );
  // With a destination the pill renders as a Link (next peak/trough →
  // Market price page, OVW-11); plain pills stay non-interactive.
  if (to) {
    return (
      <Link to={to} className={cn("block rounded-md border bg-surface-2 px-2 py-1.5 transition hover:border-accent", cls)}>
        {body}
      </Link>
    );
  }
  return (
    <div className={cn("rounded-md border bg-surface-2 px-2 py-1.5", cls)}>
      {body}
    </div>
  );
}
